/**
 * Trust Score Event Instrumentation - D-152 (C5)
 *
 * PostHog events that feed the Trust Score -- the primary metric.
 * Every event here maps to a trust-building or trust-eroding action.
 *
 * Events:
 *   trust_insight_accepted    - user acted on an insight (trust UP)
 *   trust_insight_dismissed   - user dismissed an insight (trust signal)
 *   trust_plaid_connected     - Plaid account linked (high trust)
 *   trust_gmail_connected     - Gmail integration authorized (high trust)
 *   trust_document_uploaded   - user uploaded a statement/doc (trust UP)
 *   trust_completeness_updated - profile or data completeness changed
 *   trust_session_return      - returning user with days_since_last
 *   trust_feature_first_use   - first time user accesses a feature
 */

import { track } from '../analytics.js';

const FIRST_USE_KEY = 'fynwell_feature_first_use';

function getFirstUseSet() {
  try {
    const raw = localStorage.getItem(FIRST_USE_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch {
    return new Set();
  }
}

function markFirstUse(feature) {
  try {
    const set = getFirstUseSet();
    set.add(feature);
    localStorage.setItem(FIRST_USE_KEY, JSON.stringify([...set]));
  } catch {}
}

export function trackInsightAccepted(insightId, insightType, estimatedImpact) {
  track('trust_insight_accepted', {
    insight_id: insightId,
    insight_type: insightType,
    estimated_impact: estimatedImpact,
  });
}

export function trackInsightDismissed(insightId, insightType, estimatedImpact) {
  track('trust_insight_dismissed', {
    insight_id: insightId,
    insight_type: insightType,
    estimated_impact: estimatedImpact,
  });
}

export function trackPlaidConnected(accountCount) {
  track('trust_plaid_connected', {
    accounts_count: accountCount,
  });
}

export function trackGmailConnected() {
  track('trust_gmail_connected', {});
}

export function trackDocumentUploaded(fileType, fileSize) {
  track('trust_document_uploaded', {
    file_type: fileType,
    file_size: fileSize,
  });
}

export function trackCompletenessUpdated(score, previousScore, source) {
  track('trust_completeness_updated', {
    score,
    previous_score: previousScore,
    source,
  });
}

export function trackSessionReturn() {
  try {
    const lastVisit = localStorage.getItem('aria_last_visit');
    const now = Date.now();
    const daysSince = lastVisit
      ? Math.round((now - Number(lastVisit)) / (24 * 60 * 60 * 1000))
      : null;
    track('trust_session_return', {
      days_since_last: daysSince,
      is_first_visit: daysSince === null,
    });
  } catch {
    track('trust_session_return', { days_since_last: null, is_first_visit: true });
  }
}

export function trackFeatureFirstUse(featureName) {
  const seen = getFirstUseSet();
  if (seen.has(featureName)) return;
  markFirstUse(featureName);
  track('trust_feature_first_use', {
    feature_name: featureName,
  });
}
